import {
  collection,
  documentId,
  limit as fbLimit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { runtimeDb as db } from "../../firestoreStudio/runtime/runtimeDb";

/**
 * Live refresh helpers for the Collection Explorer toolbar.
 *
 * Interval modes (10s / 30s / 1m) poll via a caller-supplied refresh function.
 * Realtime mode attaches a Firestore onSnapshot listener that streams the same
 * paginated page (orderBy + limit) — client-side filters still apply on top.
 */

export const LIVE_REFRESH_MODES = [
  { id: "off", label: "Off", intervalMs: 0, realtime: false },
  { id: "10s", label: "10 seconds", intervalMs: 10_000, realtime: false },
  { id: "30s", label: "30 seconds", intervalMs: 30_000, realtime: false },
  { id: "1m", label: "1 minute", intervalMs: 60_000, realtime: false },
  { id: "realtime", label: "Realtime", intervalMs: 0, realtime: true },
];

export function getLiveMode(id) {
  return LIVE_REFRESH_MODES.find((m) => m.id === id) || LIVE_REFRESH_MODES[0];
}

/**
 * Start an interval poller. Returns a stop() function.
 */
export function startIntervalRefresh(intervalMs, onTick) {
  if (!intervalMs || typeof onTick !== "function") return () => {};
  const handle = setInterval(() => {
    try { onTick(); } catch { /* ignore tick errors */ }
  }, intervalMs);
  return () => clearInterval(handle);
}

/**
 * Attach a realtime snapshot listener on the current collection page.
 * Returns an unsubscribe function. Errors are surfaced through onError.
 */
export function startSnapshotListener({ collectionName, sortField = "id", sortAsc = true, pageLimit = 100, onData, onError }) {
  if (!collectionName) return () => {};
  const orderTarget = sortField === "id" ? documentId() : sortField;
  const q = query(
    collection(db, collectionName),
    orderBy(orderTarget, sortAsc ? "asc" : "desc"),
    fbLimit(pageLimit)
  );

  return onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, data: d.data() }));
      if (typeof onData === "function") {
        onData({ rows, fromCache: snap.metadata?.fromCache ?? false, size: snap.size });
      }
    },
    (error) => {
      if (typeof onError === "function") onError(error);
    }
  );
}
